import { Controller } from "@hotwired/stimulus"

// A direct click on one of these performs its own behavior, so the row does not
// re-delegate it. Includes the row's primary anchor, which navigates natively.
// - Aaron, Sun Jul 26 2026
const interactiveSelector = "a, button, input, select, textarea, label, summary, [role='button'], [contenteditable='true']"

// A cell contains its own controls when one of these is present. The row's
// primary anchor is a plain `a` (never `.button`, per the component's link
// convention), so it is excluded and the space around it stays navigable, while
// button-styled action links (`a.button`) and action buttons count.
// - Aaron, Sun Jul 26 2026
const cellControlSelector = "button, a.button, input, select, textarea, label, summary, [role='button'], [contenteditable='true']"

// Connects to data-controller="table-row-links"
export default class extends Controller {
  connect() {
    this.element.addEventListener("click", this.navigate)
    this.element.addEventListener("auxclick", this.navigate)
  }

  disconnect() {
    this.element.removeEventListener("click", this.navigate)
    this.element.removeEventListener("auxclick", this.navigate)
  }

  navigate = (event) => {
    const row = event.target.closest("tr.link")
    if (!row) return

    const anchor = row.querySelector("a[href]")
    if (!anchor) return

    // Clicks on interactive descendants (the anchor itself, buttons, form
    // controls) perform their own behavior and must not be re-delegated.
    // - Aaron, Sun Jul 26 2026
    if (event.target.closest(interactiveSelector)) return

    // A near-miss beside a cell's own controls (action buttons, links, form
    // inputs) does not follow the row link. An empty cell still navigates.
    // - Aaron, Sun Jul 26 2026
    const cell = event.target.closest("td, th")
    if (cell && cell.querySelector(cellControlSelector)) return

    // A drag-to-select gesture ends in a click; honor the selection instead of
    // navigating so cell text can be copied.
    if (window.getSelection().toString().trim()) return

    if (event.type === "auxclick" && event.button !== 1) return

    const newTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1
    if (newTab) {
      window.open(anchor.href, "_blank")
    } else {
      anchor.click()
    }
  }
}
